import { exclusionCalendarDate } from './recurrenceExclusions'
import { EventInput } from '@fullcalendar/react'
import dayjs from 'dayjs'
import { t } from 'i18next'
import Database from 'services/db/Database'
import { dateInTimezone, getDefaultTimezone, resolveTimezone } from './timezone'
import {
  AccountDoc,
  DocType,
  EventDoc,
  EventStatus,
  Exclusion,
  GroupDoc,
  MasterLabelLabel,
  TaskType
} from 'types/api/changes'
import { Model } from 'types/model'

import { isConvertedGroupId } from './api/group_state'
import { eventLengthInDays, isLateInstance } from './api/instance_state'
import { is24meApp } from './appType'
import { isEventEditable } from './event'
import { eventEndTimestamp } from './eventDates'
import {
  generateGroupcalArrayFromRecurrence,
  mapRecurrency
} from './recurrence'

/**
 * Builds an rrule input for FullCalendar and preserves DTSTART;TZID in the RRULE string.
 *
 * Returns { rruleInput, exdates } where exdates is an array of naive ISO strings
 * for the separate `exdate` EventInput field.
 */
function buildFcRrule(
  event: EventDoc,
  defaultTz: string
): { rruleInput: string; exdates: string[] } | undefined {
  const rec = event.Recurrence
  if (!rec || typeof rec === 'string' || !rec.Interval || !rec.Unit)
    return undefined

  const recurrenceEndDate = Number(rec.EndDate)
  const eventStartDate = Number(event.StartDate)
  const hasMalformedRecurrenceEndDate =
    !isNaN(recurrenceEndDate) &&
    !isNaN(eventStartDate) &&
    recurrenceEndDate < eventStartDate

  // Keep malformed recurrence data as-is, but do not pass malformed RRULE to FC.
  // FC would expand zero instances for UNTIL<DTSTART and hide the event.
  if (hasMalformedRecurrenceEndDate) return undefined

  // For "nth weekday" (Unit=5) use mapRecurrency string fallback — rare pattern
  // that needs RRule.FR.nth() which doesn't map cleanly to the object form
  if (rec.Unit === 5) return undefined

  const tz =
    event.AllDay === '1'
      ? 'UTC'
      : resolveTimezone(event.TimeZoneNameID, defaultTz)

  let eventStart = dateInTimezone(Number(event.StartDate) * 1000, tz)

  // All-day iOS timezone bug: shift by 13h then snap to start of day (matches mapRecurrency)
  if (
    (event.Type === DocType.TASK || event.Type === DocType.EVENT) &&
    event.AllDay === '1' &&
    (event.TimeZoneNameID?.length === 0 || event.TimeZoneNameID !== 'UTC')
  ) {
    eventStart = eventStart.add(13, 'hour')
  }

  const rruleInput = mapRecurrency(event, true, true, defaultTz)
  if (!rruleInput) return undefined

  // Build exdate list for FullCalendar's top-level `exdate` EventInput field
  const exdates: string[] = []
  if (Array.isArray(rec.Exclusion)) {
    for (const excl of rec.Exclusion as Exclusion[]) {
      try {
        if (
          excl.Status !== EventStatus.COMPLETED &&
          excl.Status !== EventStatus.ACTIVE
        ) {
          const exclusionDate = exclusionCalendarDate(excl, tz)
          if (!exclusionDate) continue
          const exdate = `${exclusionDate}T${eventStart.format('HH:mm:ss')}`
          exdates.push(exdate)
        }
      } catch (e) {
        console.error(e)
      }
    }
  }

  return { rruleInput, exdates }
}

interface GroupStatus {
  outOfTierGroup: boolean
  canEdit: boolean
}

export function mapEventsToCalendar(
  events: Model<EventDoc>[] | EventDoc[],
  groups: Model<GroupDoc>[] | GroupDoc[],
  account: Model<AccountDoc> | AccountDoc,
  defaultTz: string,
  guest: boolean,
  somedayDate: string,
  allSomedayDate: string,
  userId: string,
  currentGroup?: string,
  referenceGroupMap?: Map<string, GroupDoc | null>,
  fullEventModelMap?: Map<string, EventDoc | Model<EventDoc>>
): EventInput[] {
  const itemsForUI = buildEventsArrayForUIFromDBEvents(
    events,
    somedayDate,
    allSomedayDate
  )

  const mapped = itemsForUI
    .filter((event) => {
      if (isConvertedGroupId(event.GroupID)) {
        return (
          event.attendees
            ?.find((attendee) => attendee.self)
            ?.responseStatus?.toLowerCase() !== 'declined'
        )
      }

      //we ignore participant status in 24me app
      if (is24meApp()) return true

      const participantStatus =
        event.ParticipantsStatus?.[userId ?? ''] ?? undefined

      return participantStatus?.ParticipantConfirmationStatus != '3'
    })
    .filter((event) => event.Status !== EventStatus.REMOVED)
    .map((event) => {
      const isAllDay = event.AllDay === '1' || event.late
      let startDate = isAllDay
        ? dayjs.utc(Number(event.StartDate) * 1000)
        : dayjs(Number(event.StartDate) * 1000).tz(defaultTz)

      const endTimestamp = eventEndTimestamp(event)

      let endDate = isAllDay
        ? dayjs.utc(Number(endTimestamp) * 1000)
        : dayjs(Number(endTimestamp) * 1000).tz(defaultTz)

      const startMillis = Number(event.StartDate) * 1000
      const endMillis = Number(endTimestamp) * 1000
      const isZeroDurationTimedEvent = !isAllDay && endMillis === startMillis
      const daysDiff = (endMillis - startMillis) / 1000 / 60 / 60 / 24

      if (isAllDay) {
        /**
         * because of mismatch between formats of all day events, we need to add extra day for our data source, in order to show
         * events correctly on a screen. The reason for that is that for example event which goes from October 9 to Octover 10
         * will be saved in our system as "from 2024-10-09 to 2024-10-10", when FullCalendar and some other providers support
         * format as such: "from 2024-10-09 to 2024-10-11", meaning, last day of the long event is day after */

        if (daysDiff > 0) {
          endDate = endDate.add(1, 'day').startOf('day')
        }

        /**
         * Because of a bug on iOS while adding all day events, they are added in devices timezone midnight as oppose to utc midnight, in order to fix that, we
         * need to add extra 13 hours to cover all timezones, and then make it start of the beginning of a day, we also can check the timezone which
         * will be empty for such events
         */

        if (
          event.TimeZoneNameID?.length === 0 ||
          event.TimeZoneNameID !== 'UTC'
        ) {
          startDate = startDate.add(13, 'hour').startOf('day')
          endDate = endDate.add(13, 'hour').startOf('day')
        }
      }

      // Build RRULE as string with DTSTART;TZID preserved.
      let rruleInput: string | undefined = undefined
      let exdates: string[] = []

      try {
        const fcRrule = buildFcRrule(event, defaultTz)
        if (fcRrule) {
          rruleInput = fcRrule.rruleInput
          exdates = fcRrule.exdates
        } else if (event.Recurrence && typeof event.Recurrence !== 'string') {
          const recurrenceEndDate = Number(event.Recurrence.EndDate)
          const eventStartDate = Number(event.StartDate)
          const hasMalformedRecurrenceEndDate =
            !isNaN(recurrenceEndDate) &&
            !isNaN(eventStartDate) &&
            recurrenceEndDate < eventStartDate

          // Keep malformed recurrence data in the model; render as a single
          // non-recurring event by skipping RRULE fallback for this case.
          if (!hasMalformedRecurrenceEndDate) {
            // Fallback: nth-weekday (Unit=5) — use legacy string form
            const rruleStr = mapRecurrency(event, true, false, defaultTz)
            if (rruleStr && rruleStr.length > 0) rruleInput = rruleStr
          }
        }
      } catch (e) {
        console.error(e)
      }

      const canEdit = isEventEditable(event, groups, account)

      const minutes = (endMillis - startMillis) / 1000 / 60

      const eventLen = eventLengthInDays(event) + 1

      const id = getFullCalendarEventId(event)
      const resolvedReferenceGroup =
        referenceGroupMap?.get(id) ??
        referenceGroupMap?.get(event.GroupID ?? '') ??
        event.referenceGroup

      let eventEditable = is24meApp() || (!guest && canEdit)

      let durationEditable =
        is24meApp() || (!guest && event.someday != '1' && canEdit)

      if (navigator.onLine === false) {
        eventEditable = false
        durationEditable = false
      }

      const notesRenderKey =
        typeof event.Notes === 'string'
          ? event.Notes
          : (event.Notes ?? []).map((n) => n?.Note ?? '').join('|')

      const reminderRenderKey =
        typeof event.Reminder === 'string'
          ? event.Reminder
          : (event.Reminder ?? [])
              .map(
                (reminder) =>
                  `${reminder?.offset ?? ''}:${reminder?.AlertTime ?? ''}:${reminder?.isRelativeReminder ?? ''}`
              )
              .join('|')

      const renderKey = [
        event._rev ?? '',
        event.Color ?? '',
        event.GroupID ?? '',
        event.OwnerID ?? '',
        event.ThirdPartyID ?? '',
        notesRenderKey,
        reminderRenderKey,
        resolvedReferenceGroup?._id ?? ''
      ].join('::')

      if (fullEventModelMap) {
        // Keep the full event outside FullCalendar, but include resolved referenceGroup
        // so Event rendering logic keeps group-based color/avatar behavior.
        fullEventModelMap.set(id, {
          ...Database.toServerObject(event as EventDoc),
          // toServerObject strips the Watermelon id, but popup edit paths for
          // unsaved events need it to update the local row and matching FC item.
          id: (event as any).id,
          // Preserve transient late fields that are not part of DB model serialization.
          late: event.late,
          lateOriginalStartTime: event.lateOriginalStartTime,
          lateOriginalEndTime: event.lateOriginalEndTime,
          referenceGroup: resolvedReferenceGroup ?? undefined
        } as EventDoc)
      }

      const data: EventInput & {
        _eventRev?: string
        _renderKey?: string
      } = {
        ...(isZeroDurationTimedEvent
          ? {}
          : {
              end: endDate.format(
                isAllDay ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss'
              )
            }),
        id,
        title: event.Text,
        start: startDate.format(
          isAllDay ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss'
        ),
        allDay: isAllDay || event.late,
        durationEditable: durationEditable,
        editable: eventEditable,
        rrule: event.late ? undefined : rruleInput,
        exdate: exdates.length > 0 ? exdates : undefined,
        duration:
          rruleInput && isAllDay
            ? { days: eventLen }
            : { minutes: minutes > 0 ? minutes : 30 },
        _eventRev: event._rev,
        _renderKey: renderKey
      }

      return data
    })

  return mapped
}

export function getFullCalendarEventId(
  event: Model<EventDoc> | EventDoc
): string {
  const stableLocalId = (event as any).id

  if (typeof stableLocalId === 'string' && stableLocalId.length > 0) {
    return stableLocalId
  }

  return `${event.late}${event._id}${event.local_id}`
}

export function buildEventsArrayForUIFromDBEvents(
  events: Model<EventDoc>[] | EventDoc[],
  somedayDate: string,
  allSomedayDate: string
) {
  let itemsForUI = is24meApp()
    ? prepareTasksForUI(events, somedayDate, allSomedayDate)
    : events

  if (is24meApp()) {
    itemsForUI = [
      ...itemsForUI,
      ...generateLateTasksForEvents(
        itemsForUI.filter(
          (event) => isLateInstance(event, true) && (event._id?.length ?? 0 > 0)
        )
      ),
      ...generateLateRecurrentInstances(itemsForUI)
    ]
    console.log(
      `[PERF] buildEventsArrayForUIFromDBEvents: input=${events.length}, output=${itemsForUI.length}`
    )
  }

  return itemsForUI
}

export function generateLateRecurrentInstances(
  arg0: Model<EventDoc>[] | EventDoc[]
) {
  const relevantItems = arg0.filter(
    (event) =>
      event.Type === DocType.TASK &&
      event.Recurrence &&
      typeof event.Recurrence !== 'string' &&
      isLateInstance(event) &&
      (event._id?.length ?? 0 > 0)
  )

  const lateInstances: any = []

  // convert this map to a for loop

  for (let i = 0; i < relevantItems.length; i++) {
    const event = relevantItems[i]
    const parsedItems = generateGroupcalArrayFromRecurrence(
      event,
      true,
      undefined,
      undefined,
      getDefaultTimezone()
    )

    if (
      parsedItems.some(
        (event) =>
          dayjs(Number(event.StartDate) * 1000)
            .tz(getDefaultTimezone())
            .format('YYYY-MM-DD') ===
          dayjs().tz(getDefaultTimezone()).format('YYYY-MM-DD')
      )
    )
      continue

    const filteredItems = parsedItems.filter((item) => isLateInstance(item))
    const reversedItems = filteredItems.reverse()
    const uniqueItems = reversedItems.filter(
      (item: EventDoc, index: number, self: EventDoc[]) =>
        index === self.findIndex((event: EventDoc) => event._id === item._id)
    )
    const instance = uniqueItems[0]
    if (
      instance &&
      (instance.Status === EventStatus.ACTIVE ||
        instance.Status === EventStatus.COMPLETED)
    )
      lateInstances.push(createLateFromEvent(instance))
    else continue
  }
  return lateInstances
}

export function createLateFromEvent(event: EventDoc): EventDoc {
  const now = dayjs().tz('UTC')

  const lateStart = now.startOf('day')

  return {
    ...event,
    ...Database.toServerObject(event),
    StartDate: lateStart.unix().toString(),
    EndDate: lateStart.add(1, 'second').unix().toString(),
    TimeZoneNameID: event.TimeZoneNameID,
    late: true,
    lateOriginalStartTime: event.StartDate,
    lateOriginalEndTime: event.EndDate
  }
}

export function generateLateTasksForEvents(events: EventDoc[]): EventDoc[] {
  const late = events
    .filter((event) => isLateInstance(event) && event.Type === DocType.TASK)
    .map((event) => {
      return createLateFromEvent(event)
    })

  return late
}

export function prepareTasksForUI(
  events: EventDoc[] | undefined,
  somedayDate: string,
  allSomedayDate: string,
  masterLabelLabel?: MasterLabelLabel,
  showingDocType?: DocType
): EventDoc[] {
  if (!events) return []
  const filtered = events
    .filter((event) => {
      return event.TaskType != TaskType.birthday
    })
    .filter((event) => {
      if (!masterLabelLabel) return true // return all items

      if (event.Label && typeof event.Label !== 'string') {
        return (
          event.Label.some(
            (ele) =>
              ele.LabelText === masterLabelLabel?.ID &&
              event.Type === showingDocType
          ) && event.someday !== '1'
        )
      }
    })

  const compositeSomedayTask = makeCompositeSomedayTask(
    filtered,
    somedayDate,
    allSomedayDate
  )

  return compositeSomedayTask
    ? [
        ...filtered.filter((event) => {
          return event.someday != '1'
        }),
        compositeSomedayTask
      ]
    : filtered
}

export function makeCompositeSomedayTask(
  events: EventDoc[],
  somedayDate: string,
  allSomedayDate: string
) {
  const somedayTask = events.filter((event) => {
    return (
      event.someday === '1' ||
      (event.StartDate === 'null' && event.EndDate === 'null')
    )
  })

  let compositeSomedayTask: Model<EventDoc> | EventDoc | undefined = undefined

  if (somedayTask && somedayTask.length > 0 && is24meApp()) {
    let start = dayjs().tz('UTC').startOf('day')
    let end = start.add(1, 'second')

    if (somedayDate) {
      start = dayjs.unix(Number(somedayDate))
      end = start.add(1, 'second')
    }

    compositeSomedayTask = {
      ...somedayTask[0],
      ...Database.toServerObject(somedayTask[0]),
      Text: t('somedayTask', { amount: somedayTask.length }) ?? '',
      Label: undefined,
      someday: '1',
      AllDay: allSomedayDate ? (allSomedayDate ?? '0') : '1',
      Status: EventStatus.ACTIVE,
      Priority: '0',
      StartDate: start.unix().toString(),
      EndDate: end.unix().toString(),
      TaskType: TaskType.meet,
      Type: DocType.TASK,
      _id: `s${somedayTask[0]._id}`
    }
  }
  return compositeSomedayTask
}
